import { useEffect, useMemo, useState } from 'react';

import { useDimensions, useSaveDimensions } from '@/api/queries';
import { useSession } from '@/session/SessionContext';
import { ACTIVE_MAX, draftOf, draftStatus, saveBody, type Draft, type Fields } from './dimensionsDraft';

let nextLocalId = 0;

/** The dimensions, the local draft of them, and the dialogs open over it. */
export function useDimensionsSection() {
  const dimensions = useDimensions();
  const save = useSaveDimensions();
  const editable = useSession().can('research.dimensions.manage');
  const rows = useMemo(() => dimensions.data?.dimensions ?? [], [dimensions.data]);
  const stored = useMemo(() => draftOf(rows), [rows]);
  const [draft, setDraft] = useState<Draft[]>(stored);
  const [editing, setEditing] = useState<Draft | 'new' | null>(null);
  const [confirming, setConfirming] = useState(false);
  useEffect(() => setDraft(stored), [stored]);

  const status = draftStatus(stored, draft);
  const { active } = status;
  const retiredCount = rows.filter((d) => d.retired_at !== null).length;
  const canSave = status.changed && status.problems.length === 0 && status.total === 100 && !save.isPending;

  const update = (id: string, patch: Partial<Draft>) => setDraft((all) => all.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  // A row added in this draft has nothing to retire: it just goes.
  const discard = (row: Draft) =>
    row.key ? update(row.id, { retire: true }) : setDraft((all) => all.filter((d) => d.id !== row.id));
  const takenBy = (except: string | null) => active.filter((d) => d.id !== except).map((d) => d.label.trim().toLowerCase());

  const apply = (fields: Fields) => {
    if (editing === 'new') setDraft((all) => [...all, { id: `new-${(nextLocalId += 1)}`, ...fields, retire: false }]);
    else if (editing) update(editing.id, fields);
    setEditing(null);
  };

  const reset = () => setDraft(stored);

  const submit = () => save.mutate({ dimensions: saveBody(active) }, { onSuccess: () => setConfirming(false) });

  return {
    dimensions,
    save,
    editable,
    draft,
    editing,
    setEditing,
    confirming,
    setConfirming,
    ...status,
    retiredCount,
    canSave,
    addDisabled: active.length >= ACTIVE_MAX,
    update,
    discard,
    takenBy,
    apply,
    reset,
    submit,
  };
}

export type DimensionsState = ReturnType<typeof useDimensionsSection>;
